import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Typography,
  IconButton,
  InputBase,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CampaignIcon from "@mui/icons-material/Campaign";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import plusIcon from "../../assets/images/plus.svg";
import "../../styles/Listing/listing-premium.css";
import "../../styles/CampaignLeads/campaign-leads-drawer.css";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { deleteRequest, getRequest, postRequest } from "../../api/apiServices";
import {
  compaignFormList,
  deleteCompaignForm,
  getVonqCampaignDetails,
  markVonqCampaignOffline,
  markVonqCampaignChannelOffline,
} from "../../api/apiList";
import moment from "moment";
import Loader from "../../utils/Loading";
import Toast from "../../utils/Toast";
import PaginationComponent from "../../utils/PaginationComponent";
import ConfirmationDialog from "../../components/DailogBox";
import CampaignLeadsDrawer, {
  CampaignLeadsDrawerCampaign,
} from "../../components/CampaignLeads/CampaignLeadsDrawer";

export {};

interface Campaign {
  id: number;
  name: string;
  createdAt?: string;
  campaignId: string;
  price: string;
  campaignStatus?: string;
  paymentStatus?: string;
  isActive?: boolean;
  campaignStart?: string;
}

interface CampaignChannel {
  id?: string | number;
  productId?: string | number;
  channelName?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  clicks?: number;
  boardLink?: string;
}

interface CampaignDetail {
  jobId?: string;
  campaignId?: string;
  campaignStatus?: string;
  paymentStatus?: string;
  orderDate?: string;
  lastModified?: string;
  totalBudget?: string | number;
  jobDescriptionUrl?: string;
  targetTags?: string[];
  channels?: CampaignChannel[];
  targetAudienceSummary?: string[];
  jobTitle?: string;
  raw?: any;
}

const formatDateTime = (value?: string) => {
  if (!value) return "Not delivered yet";
  const momentValue = moment(value);
  if (!momentValue.isValid()) return value;
  return momentValue.format("MMM DD, YYYY h:mm A");
};

const extractChannels = (raw: any = {}): CampaignChannel[] => {
  const payload = raw?.campaignPayload || raw?.payload || {};
  const possibleSources = [
    raw?.channels,
    raw?.channelDetails,
    raw?.vonqChannels,
    raw?.orderedProductsDetails,
    raw?.orderedProductsSpecs,
    payload?.orderedProductsDetails,
    payload?.orderedProductsSpecs,
    raw?.campaignChannels,
  ];

  const source = possibleSources.find(
    (item: any) => Array.isArray(item) && item.length
  );

  if (!Array.isArray(source)) return [];

  return source.map((channel: any, index: number) => {
    const channelName =
      channel?.channelName ||
      channel?.name ||
      channel?.productName ||
      channel?.title ||
      channel?.boardName ||
      `Channel ${index + 1}`;

    const clicksRaw =
      channel?.clicks ?? channel?.clickCount ?? channel?.click_count;
    const clicks =
      typeof clicksRaw === "number"
        ? clicksRaw
        : Number.isFinite(Number(clicksRaw))
        ? Number(clicksRaw)
        : undefined;

    return {
      id: channel?.id || channel?.productId || index,
      productId: channel?.productId || channel?.id,
      channelName,
      status:
        channel?.status ||
        channel?.deliveryStatus ||
        channel?.state ||
        channel?.channelStatus,
      startDate:
        channel?.startDate ||
        channel?.start_date ||
        channel?.deliveryStart ||
        channel?.start,
      endDate:
        channel?.endDate ||
        channel?.end_date ||
        channel?.deliveryEnd ||
        channel?.end,
      clicks,
      boardLink:
        channel?.boardLink ||
        channel?.jobPageUrl ||
        channel?.jobBoardUrl ||
        channel?.url ||
        channel?.link,
    };
  });
};

const buildTargetTags = (raw: any = {}): string[] => {
  const payload = raw?.campaignPayload || raw?.payload || {};
  const postingDetails =
    payload?.postingDetails || raw?.postingDetails || raw?.jobDetails || {};
  const targetGroup =
    payload?.targetGroup || raw?.targetGroup || raw?.targetAudience || {};
  const additionalTags =
    raw?.tags ||
    raw?.labels ||
    raw?.targetAudienceSummary ||
    raw?.targeting ||
    [];
  const flattenedAdditional =
    Array.isArray(additionalTags) && additionalTags.length
      ? additionalTags
      : [];
  const tags = [
    postingDetails?.title,
    postingDetails?.employmentType,
    ...(targetGroup?.educationLevel || []).map(
      (item: any) => item?.description
    ),
    ...(targetGroup?.seniority || []).map((item: any) => item?.description),
    ...(targetGroup?.jobCategory || []).map((item: any) => item?.description),
    ...(targetGroup?.industry || []).map((item: any) => item?.description),
    ...(targetGroup?.audience || []).map((item: any) => item?.name),
    ...flattenedAdditional,
  ].filter(Boolean);

  return Array.from(new Set(tags));
};

const normalizeCampaignDetail = (raw: any): CampaignDetail => {
  if (!raw) return {};
  const payload = raw?.campaignPayload || raw?.payload || {};
  const postingDetails =
    payload?.postingDetails || raw?.postingDetails || raw?.jobDetails || {};
  const targetAudienceSummary =
    raw?.targetAudienceSummary ||
    raw?.targetAudience ||
    payload?.targetGroup ||
    null;
  const audienceSummaryList = Array.isArray(targetAudienceSummary)
    ? targetAudienceSummary
    : targetAudienceSummary && typeof targetAudienceSummary === "object"
    ? Object.values(targetAudienceSummary)
        .flat()
        .map((item: any) => {
          if (typeof item === "string") return item;
          if (item?.description) return item.description;
          if (item?.name) return item.name;
          return null;
        })
        .filter(Boolean)
    : [];

  return {
    jobId: raw?.campaignId || payload?.campaignId,
    campaignId: raw?.campaignId || payload?.campaignId,
    campaignStatus: raw?.campaignStatus || raw?.status,
    paymentStatus: raw?.paymentStatus || raw?.payment_status,
    orderDate: raw?.createdAt || raw?.orderDate || raw?.startDate,
    lastModified: raw?.updatedAt || raw?.lastModified || raw?.endDate,
    totalBudget: raw?.price || raw?.totalBudget || raw?.budget,
    jobDescriptionUrl:
      raw?.jobDescriptionUrl ||
      postingDetails?.jobPageUrl ||
      postingDetails?.jobUrl,
    targetTags: buildTargetTags(raw),
    targetAudienceSummary: audienceSummaryList,
    jobTitle: postingDetails?.title || raw?.jobTitle,
    channels: extractChannels(raw),
    raw,
  };
};

const getStatusClass = (status?: string) => {
  const normalized = status?.toLowerCase().trim() || "";

  if (!normalized || normalized === "n/a") {
    return "listing-status-chip--default";
  }

  if (
    normalized.includes("active") ||
    normalized.includes("online") ||
    normalized.includes("paid") ||
    normalized.includes("completed")
  ) {
    return "listing-status-chip--active";
  }

  if (
    normalized.includes("pending") ||
    normalized.includes("processing") ||
    normalized.includes("created") ||
    normalized.includes("unpaid") ||
    normalized.includes("progress")
  ) {
    return "listing-status-chip--warning";
  }

  if (normalized.includes("failed") || normalized.includes("offline")) {
    return "listing-status-chip--danger";
  }

  return "listing-status-chip--default";
};

const formatStatusLabel = (status?: string) => {
  if (!status || status === "N/A") return "N/A";

  return status
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const formatAmount = (value?: string | number) => {
  if (value === undefined || value === null || value === "") {
    return "Not available";
  }

  if (typeof value === "number") {
    return `€${value.toLocaleString()}`;
  }

  const numericValue = Number(value);
  if (!Number.isNaN(numericValue)) {
    return `€${numericValue.toLocaleString()}`;
  }

  return value;
};

const StatusChip = ({ label }: { label?: string }) => (
  <span className={`listing-status-chip ${getStatusClass(label)}`}>
    {formatStatusLabel(label)}
  </span>
);

interface CampaignDrawerSectionProps {
  detail?: CampaignDetail;
  fallback: Campaign;
  campaignGuid?: string;
  onRequestClose: () => void;
  onCampaignOffline?: () => void;
  campaignOfflineLoading?: boolean;
  onChannelOffline?: (
    productId?: string | number,
    channelName?: string
  ) => void;
  channelOfflineLoadingKey?: string | null;
}

const CampaignDrawerSection = ({
  detail,
  fallback,
  campaignGuid,
  onRequestClose,
  onCampaignOffline,
  campaignOfflineLoading,
  onChannelOffline,
  channelOfflineLoadingKey,
}: CampaignDrawerSectionProps) => {
  const tags =
    detail?.targetAudienceSummary && detail.targetAudienceSummary.length
      ? detail.targetAudienceSummary
      : detail?.targetTags && detail.targetTags.length > 0
      ? detail.targetTags
      : ([
          fallback?.name,
          fallback?.campaignStatus,
          fallback?.paymentStatus,
        ].filter(Boolean) as string[]);

  const resolvedCampaignGuid =
    campaignGuid || detail?.campaignId || fallback?.campaignId;

  const summaryItems = [
    {
      label: "Order date",
      value: formatDateTime(detail?.orderDate || fallback?.createdAt),
    },
    {
      label: "Last modified",
      value: formatDateTime(detail?.lastModified || fallback?.createdAt),
    },
    {
      label: "Budget",
      value: formatAmount(detail?.totalBudget || fallback?.price),
    },
    {
      label: "Campaign ID",
      value: detail?.campaignId || fallback?.campaignId,
    },
    {
      label: "Job title",
      value: detail?.jobTitle || fallback?.name,
    },
    {
      label: "Job description URL",
      value: detail?.jobDescriptionUrl,
      isLink: true,
    },
  ];

  const channels = (detail?.channels || []).map((channel) => ({
    ...channel,
  }));

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        gap={2}
        flexWrap="wrap"
      >
        <Box>
          <Typography fontSize="20px" fontWeight={600} color="#111928">
            {fallback?.name || "Campaign details"}
          </Typography>
          <Typography fontSize="14px" color="#6B7280" mt={0.5}>
            Job ID: {detail?.jobId || fallback?.campaignId || "N/A"}
          </Typography>
          {!detail && (
            <Typography fontSize="13px" color="#9CA3AF" mt={0.5}>
              Live insights are still loading. Showing basic information for
              now.
            </Typography>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <StatusChip
            label={detail?.campaignStatus || fallback?.campaignStatus}
          />
          <StatusChip
            label={detail?.paymentStatus || fallback?.paymentStatus}
          />
          {onCampaignOffline && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={campaignOfflineLoading}
              onClick={onCampaignOffline}
              sx={{
                textTransform: "none",
                borderRadius: "999px",
                px: 2.5,
                fontWeight: 600,
                borderColor: "rgba(239,68,68,0.4)",
                "&:hover": {
                  borderColor: "#EF4444",
                  backgroundColor: "rgba(239,68,68,0.08)",
                },
              }}
            >
              {campaignOfflineLoading ? (
                <CircularProgress size={16} />
              ) : (
                "Take offline"
              )}
            </Button>
          )}
          <Button
            variant="text"
            size="small"
            onClick={onRequestClose}
            sx={{ textTransform: "none" }}
          >
            Hide details
          </Button>
        </Box>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2} mt={3}>
        {summaryItems.map((item) => (
          <Box
            key={item.label}
            minWidth="220px"
            flex={1}
            p={2.5}
            borderRadius="18px"
            border="1px solid rgba(148,163,184,0.35)"
            sx={{
              background: "linear-gradient(180deg,#FFFFFF 0%,#F8FAFC 100%)",
              boxShadow:
                "0px 15px 35px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,0.4)",
              transition: "transform 0.25s ease, box-shadow 0.25s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow:
                  "0px 25px 45px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.7)",
              },
            }}
          >
            <Typography
              fontSize="11px"
              color="#94A3B8"
              textTransform="uppercase"
              letterSpacing="0.2em"
            >
              {item.label}
            </Typography>
            {item.isLink && item.value ? (
              <Typography
                component="a"
                href={item.value as string}
                target="_blank"
                rel="noopener noreferrer"
                fontSize="16px"
                fontWeight={700}
                color="#2563EB"
                sx={{ textDecoration: "none" }}
              >
                View job description
              </Typography>
            ) : (
              <Typography fontSize="16px" fontWeight={700} color="#0F172A">
                {item.value || "Not available"}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography fontSize="14px" fontWeight={600} color="#111928" mb={1}>
        Target audience
      </Typography>
      {tags.length ? (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              sx={{ backgroundColor: "#EEF2FF", color: "#312E81" }}
            />
          ))}
        </Box>
      ) : (
        <Typography fontSize="14px" color="#6B7280">
          No audience details available.
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography fontSize="16px" fontWeight={600} color="#111928" mb={1}>
        Channels
      </Typography>

      <Box
        display="flex"
        flexDirection="column"
        border="1px solid #E5E7EB"
        borderRadius="12px"
        overflow="hidden"
      >
        <Box
          display="flex"
          bgcolor="#F3F4F6"
          py={1.5}
          px={2}
          fontSize="14px"
          fontWeight={600}
          color="#6B7280"
        >
          <Box width="25%">Channel</Box>
          <Box width="15%">Status</Box>
          <Box width="15%">Start date</Box>
          <Box width="15%">End date</Box>
          <Box width="10%">Clicks</Box>
          <Box width="20%">Board link</Box>
          <Box width="10%" textAlign="right">
            Actions
          </Box>
        </Box>
        {channels.length ? (
          channels.map((channel) => {
            const channelKey =
              resolvedCampaignGuid && channel.productId
                ? `${resolvedCampaignGuid}-${channel.productId}`
                : "";
            const isChannelLoading =
              !!channelKey && channelOfflineLoadingKey === channelKey;
            const canTakeOffline =
              Boolean(onChannelOffline) && Boolean(channel.productId);
            return (
              <Box
                key={channel.id || channel.channelName}
                display="flex"
                alignItems="center"
                py={1.5}
                px={2}
                borderTop="1px solid #F3F4F6"
                fontSize="14px"
                sx={{
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 8px 18px rgba(15,23,42,0.08)",
                  },
                }}
              >
                <Box width="25%" fontWeight={600} color="#111928">
                  {channel.channelName}
                </Box>
                <Box width="15%">
                  <StatusChip
                    label={channel.status || detail?.campaignStatus}
                  />
                </Box>
                <Box width="15%">{formatDateTime(channel.startDate)}</Box>
                <Box width="15%">{formatDateTime(channel.endDate)}</Box>
                <Box width="10%">{channel.clicks ?? 0}</Box>
                <Box width="20%">
                  {channel.boardLink ? (
                    <Typography
                      component="a"
                      href={channel.boardLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="#2563EB"
                      sx={{ textDecoration: "none" }}
                    >
                      Open link
                    </Typography>
                  ) : (
                    "Not available"
                  )}
                </Box>
                <Box width="10%" display="flex" justifyContent="flex-end">
                  {onChannelOffline && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={!canTakeOffline || isChannelLoading}
                      onClick={(event) => {
                        event.stopPropagation();
                        onChannelOffline(
                          channel.productId,
                          channel.channelName
                        );
                      }}
                      sx={{
                        textTransform: "none",
                        borderRadius: "999px",
                        px: 2,
                        minWidth: "fit-content",
                      }}
                    >
                      {isChannelLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        "Take offline"
                      )}
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })
        ) : (
          <Box py={3} px={2}>
            <Typography fontSize="14px" color="#6B7280">
              Channel details will appear here once the campaign is delivered.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const CampaignListing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Adjust breakpoint if necessary
  const isTabs = useMediaQuery("(max-width:1200px)");
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [expandedCampaignId, setExpandedCampaignId] = useState<number | null>(
    null
  );
  const [drawerLoadingId, setDrawerLoadingId] = useState<number | null>(null);
  const [campaignDetailsCache, setCampaignDetailsCache] = useState<
    Record<number, CampaignDetail>
  >({});
  const [campaignOfflineLoadingId, setCampaignOfflineLoadingId] = useState<
    string | null
  >(null);
  const [channelOfflineLoadingKey, setChannelOfflineLoadingKey] = useState<
    string | null
  >(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState<{
    type: "campaign" | "channel";
    campaignId?: string;
    productId?: string | number;
    rowId?: number;
    title: string;
    message: string;
  } | null>(null);
  const [loader, setLoader] = useState(false);
  const tableRef = useRef(null);
  const [leadsDrawerCampaign, setLeadsDrawerCampaign] =
    useState<CampaignLeadsDrawerCampaign | null>(null);
  const [leadsDrawerOpen, setLeadsDrawerOpen] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10); // limit
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  // Pagination handlers
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Debounce Timer
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  //Searching campaign
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    // Clear the previous debounce timer
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    // Set a new debounce timer
    debounceTimer.current = setTimeout(() => {
      fetchCompaignData(page, rowsPerPage, value); // Fetch campaigns with the updated search term
    }, 300);
  };

  // Fetch Campaign Data
  const fetchCompaignData = async (
    page: number,
    limit: number,
    search: string
  ) => {
    // if (searchTerm) setLoader(true);
    setLoader(true);
    const response = await postRequest(compaignFormList, {
      page,
      limit,
      search: search,
      searchColumns: ["campaignName"],
    });

    if (response?.data?.success) {
      const apiData = response.data.data;

      setCampaign(
        (apiData?.campaigns || []).map((c: any) => ({
          id: c.id,
          name: c.campaignName,
          campaignId: c.campaignId,
          createdAt: c.createdAt || c.orderDate,
          price: c.price,
          campaignStatus: c.campaignStatus || c.status,
          paymentStatus: c.paymentStatus || c.payment_status,
          isActive: c.isActive,
        }))
      );
      setCount(apiData?.count || 0);
      setPage((apiData?.page || 1) - 1);
      setHasNextPage(apiData?.hasNextPage || false);
      setHasPreviousPage(apiData?.hasPreviousPage || false);
    }
    setLoader(false);
  };

  const fetchCampaignDetail = async (rowId: number, campaignGuid?: string) => {
    if (!campaignGuid) {
      Toast("Campaign identifier missing", "error");
      return;
    }

    setDrawerLoadingId(rowId);
    try {
      const response = await getRequest(getVonqCampaignDetails(campaignGuid));
      const detailPayload = response?.data?.data || response?.data;
      if (detailPayload) {
        setCampaignDetailsCache((prev) => ({
          ...prev,
          [rowId]: normalizeCampaignDetail(detailPayload),
        }));
      } else {
        Toast("Unable to load campaign details", "error");
      }
    } catch (error) {
      Toast("Unable to load campaign details", "error");
    } finally {
      setDrawerLoadingId((prev) => (prev === rowId ? null : prev));
    }
  };

  const handleCampaignRowClick = (item: Campaign) => {
    const rowId = item.id;
    const campaignGuid = item.campaignId;
    const isOpening = expandedCampaignId !== rowId;
    setExpandedCampaignId(isOpening ? rowId : null);
    if (isOpening && !campaignDetailsCache[rowId]) {
      fetchCampaignDetail(rowId, campaignGuid);
    }
  };

  const runCampaignOffline = async (rowId: number, campaignGuid: string) => {
    setCampaignOfflineLoadingId(campaignGuid);
    try {
      await getRequest(markVonqCampaignOffline(campaignGuid));
      Toast("Campaign marked offline");
      await fetchCampaignDetail(rowId, campaignGuid);
      fetchCompaignData(page + 1, rowsPerPage, searchTerm);
    } catch (error) {
      Toast("Failed to update campaign status", "error");
    } finally {
      setCampaignOfflineLoadingId((prev) =>
        prev === campaignGuid ? null : prev
      );
    }
  };

  const runChannelOffline = async (
    rowId: number,
    campaignGuid: string,
    productId: string | number
  ) => {
    const key = `${campaignGuid}-${productId}`;
    setChannelOfflineLoadingKey(key);
    try {
      await postRequest(markVonqCampaignChannelOffline, {
        campaignId: campaignGuid,
        productId,
      });
      Toast("Channel marked offline");
      await fetchCampaignDetail(rowId, campaignGuid);
    } catch (error) {
      Toast("Failed to update channel status", "error");
    } finally {
      setChannelOfflineLoadingKey((prev) => (prev === key ? null : prev));
    }
  };

  const requestCampaignOffline = (rowId: number, campaignGuid?: string) => {
    if (!campaignGuid) {
      Toast("Campaign identifier missing", "error");
      return;
    }
    setConfirmationConfig({
      type: "campaign",
      rowId,
      campaignId: campaignGuid,
      title: "Take campaign offline?",
      message:
        "This will stop the campaign across all channels. Are you sure you want to continue?",
    });
  };

  const requestChannelOffline = (
    rowId: number,
    campaignGuid?: string,
    productId?: string | number,
    channelName?: string
  ) => {
    if (!campaignGuid || !productId) {
      Toast("Channel identifiers missing", "error");
      return;
    }
    setConfirmationConfig({
      type: "channel",
      rowId,
      campaignId: campaignGuid,
      productId,
      title: "Take channel offline?",
      message: `This will take ${
        channelName || "this channel"
      } offline. Continue?`,
    });
  };

  const handleConfirmationClose = () => setConfirmationConfig(null);

  const handleConfirmationConfirm = async () => {
    if (!confirmationConfig || confirmationConfig.rowId === undefined) return;
    const { type, rowId, campaignId, productId } = confirmationConfig;
    handleConfirmationClose();
    if (type === "campaign" && campaignId) {
      await runCampaignOffline(rowId, campaignId);
    } else if (type === "channel" && campaignId && productId !== undefined) {
      await runChannelOffline(rowId, campaignId, productId);
    }
  };

  const handleDeleteClick = (id: number) => {
    setSelectedCampaign(id);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedCampaign(null);
  };

  // delete campaign data
  const handleDelete = async () => {
    if (selectedCampaign !== null) {
      const response = await deleteRequest(
        deleteCompaignForm(selectedCampaign)
      );
      if (response) {
        Toast(response?.data?.statusMessage || "Campaign Deleted Successfully");
        fetchCompaignData(page, rowsPerPage, searchTerm);
      }
      setOpenDialog(false);
    }
  };

  const OpenCompaignForm = () => {
    navigate("/campaigns/create-campaigns");
  };

  const editCompainFormNavigate = (id: number) => {
    navigate(`/campaigns/edit-campaigns/${id}`);
  };

  const openLeadsDrawer = (jobItem: Campaign) => {
    setLeadsDrawerCampaign({
      campaignId: jobItem.campaignId,
      name: jobItem.name,
    });
    setLeadsDrawerOpen(true);
  };

  const closeLeadsDrawer = () => {
    setLeadsDrawerOpen(false);
    setLeadsDrawerCampaign(null);
  };

  useEffect(() => {
    if (!searchLoading) {
      fetchCompaignData(page + 1, rowsPerPage, searchTerm);
    }
  }, [page, rowsPerPage]);

  const inputStyle = {
    backgroundColor: "#ffffff",
    padding: "6px 12px",
    borderRadius: "8px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
    height: "40px",
  };

  return (
    <>
      <Box className="listing-page">
        <Box className="listing-hero">
          <Box className="listing-hero-inner">
            <Box>
              <Box className="listing-hero-badge">
                <CampaignIcon sx={{ fontSize: 14 }} />
                Campaigns
              </Box>
              <h1 className="listing-hero-title">Campaigns Management</h1>
              <p className="listing-hero-subtitle">
                Create and manage all your recruitment campaigns
              </p>
            </Box>

            <Box className="listing-hero-actions">
              <button type="button" className="listing-hero-btn" onClick={OpenCompaignForm}>
                <AddIcon sx={{ fontSize: 18 }} />
                Create New Campaign
              </button>

              <Box className="listing-hero-search">
                <SearchIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 20 }} />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box ref={tableRef}>
          {loader ? (
            <Loader />
          ) : (
            <>
              {campaign.length === 0 ? (
                <Box className="listing-empty">
                  <Box className="listing-empty-icon">
                    <CampaignIcon />
                  </Box>
                  <h2 className="listing-empty-title">No Campaigns Found</h2>
                  <p className="listing-empty-text">
                    {searchTerm
                      ? `No campaigns found matching "${searchTerm}". Try adjusting your search.`
                      : "Create your first campaign to start recruiting."}
                  </p>
                </Box>
              ) : (
                <Box className="listing-list">
                  {campaign?.map((jobItem, index) => {
                    const isExpanded = expandedCampaignId === jobItem.id;
                    return (
                      <Box key={jobItem.id}>
                        {/* Card */}
                        <Box
                          onClick={() => handleCampaignRowClick(jobItem)}
                          className={`listing-card${isExpanded ? " listing-card--active" : ""}`}
                          sx={{ cursor: "pointer" }}
                        >
                          <Box className="listing-card-body">
                            <Box className="listing-card-info">
                              <Box className="listing-card-title-row">
                                <Box className="listing-card-index">
                                  {index + 1 + page * rowsPerPage}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <p className="listing-card-title">
                                    {jobItem.name || "N/A"}
                                  </p>
                                  <Box className="listing-card-subtitle">
                                    ID: {jobItem.campaignId}
                                  </Box>
                                </Box>
                              </Box>

                              <Box className="listing-meta-row">
                                <StatusChip label={jobItem.campaignStatus || "N/A"} />
                                <StatusChip label={jobItem.paymentStatus || "N/A"} />
                              </Box>
                            </Box>

                            <Box className="listing-actions">
                              <button
                                type="button"
                                className="campaign-leads-view-btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openLeadsDrawer(jobItem);
                                }}
                              >
                                <PeopleOutlineIcon sx={{ fontSize: 18 }} />
                                View Candidates
                              </button>
                              <IconButton
                                aria-label="edit campaign"
                                className="listing-action-btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  editCompainFormNavigate(jobItem.id);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                aria-label="toggle campaign details"
                                className="listing-action-btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleCampaignRowClick(jobItem);
                                }}
                              >
                                <KeyboardArrowDownIcon
                                  sx={{
                                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.3s ease",
                                  }}
                                />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              background:
                                "linear-gradient(180deg,#FFFFFF 0%,#F8FAFC 100%)",
                              borderRadius: "20px",
                              border: "1px solid #E2E8F0",
                              px: { xs: 2, md: 4 },
                              py: 3,
                              mt: 2,
                              boxShadow: "0 20px 45px rgba(15,23,42,0.08)",
                            }}
                          >
                            {drawerLoadingId === jobItem.id ? (
                              <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                py={3}
                              >
                                <CircularProgress size={32} />
                              </Box>
                            ) : (
                              <CampaignDrawerSection
                                detail={campaignDetailsCache[jobItem.id]}
                                fallback={jobItem}
                                campaignGuid={jobItem.campaignId}
                                onRequestClose={() =>
                                  setExpandedCampaignId(null)
                                }
                                onCampaignOffline={() =>
                                  requestCampaignOffline(
                                    jobItem.id,
                                    jobItem.campaignId
                                  )
                                }
                                campaignOfflineLoading={
                                  campaignOfflineLoadingId ===
                                  (campaignDetailsCache[jobItem.id]
                                    ?.campaignId || jobItem.campaignId)
                                }
                                onChannelOffline={(productId, channelName) =>
                                  requestChannelOffline(
                                    jobItem.id,
                                    jobItem.campaignId,
                                    productId,
                                    channelName
                                  )
                                }
                                channelOfflineLoadingKey={
                                  channelOfflineLoadingKey
                                }
                              />
                            )}
                          </Box>
                        </Collapse>

                      </Box>
                    );
                  })}
                </Box>
              )}

              {campaign && campaign.length > 0 && (
                <Box className="listing-pagination-wrap">
                  <PaginationComponent
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    count={count}
                    hasNextPage={hasNextPage}
                    hasPreviousPage={hasPreviousPage}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <CampaignLeadsDrawer
        open={leadsDrawerOpen}
        campaign={leadsDrawerCampaign}
        onClose={closeLeadsDrawer}
      />
      <ConfirmationDialog
        open={openDialog}
        onConfirm={handleDelete}
        onCancel={handleClose}
        title="Confirm Deletion"
        message="Are you sure you want to permanently delete this campaign?"
        cancelButtonName="Cancel"
        confirmButtonName="Delete"
      />
      <ConfirmationDialog
        open={Boolean(confirmationConfig)}
        onConfirm={handleConfirmationConfirm}
        onCancel={handleConfirmationClose}
        title={confirmationConfig?.title || "Confirm Action"}
        message={confirmationConfig?.message || ""}
        cancelButtonName="Cancel"
        confirmButtonName="Yes, continue"
      />
    </>
  );
};

export default CampaignListing;
